import React, { useState } from 'react';
import './HotspotQuestion.css';

/**
 * HotspotQuestion — renders a hotspot-dropdown question.
 *
 * Each slot (description/task) gets its own dropdown populated from the
 * shared options bank. On submit, every slot is checked against correctPairs.
 *
 * Props:
 *   question  {string}   — the question stem text
 *   hotspot   {object}   — { slots, options, correctPairs }
 *   imagePath {string}   — optional question image URL
 *   onResult  {function} — called with { isCorrect, slotResults, explanation }
 *   disabled  {boolean}  — true after submission (review mode)
 *   revealed  {object}   — pre-filled result for review mode
 */
function HotspotQuestion({ question, hotspot, imagePath, onResult, disabled = false, revealed = null }) {
  const { slots = [], options = [], correctPairs = [] } = hotspot || {};

  // Map slot text → correct answer for fast lookup.
  const correctMap = Object.fromEntries(
    correctPairs.map((p) => [p.slot, p.answer])
  );

  const [selections, setSelections] = useState(
    () => Object.fromEntries(slots.map((s) => [s, '']))
  );

  const [result, setResult] = useState(revealed || null);

  const allSelected = slots.every((s) => selections[s] !== '');

  const handleChange = (slot, value) => {
    if (disabled || result) return;
    setSelections((prev) => ({ ...prev, [slot]: value }));
  };

  const handleSubmit = () => {
    if (!allSelected || result) return;

    const slotResults = slots.map((slot) => ({
      slot,
      selected: selections[slot],
      correct: correctMap[slot] || '',
      isCorrect: selections[slot] === correctMap[slot],
    }));

    const allCorrect = slotResults.every((r) => r.isCorrect);
    const res = { isCorrect: allCorrect, slotResults };
    setResult(res);
    if (onResult) onResult(res);
  };

  const handleReset = () => {
    if (disabled) return;
    setSelections(Object.fromEntries(slots.map((s) => [s, ''])));
    setResult(null);
  };

  return (
    <div className="hotspot-question">
      {imagePath && (
        <div className="hotspot-image-wrap">
          <img src={imagePath} alt="Question diagram" className="hotspot-img" />
        </div>
      )}

      <div className="hotspot-table-wrap">
        <table className="hotspot-table" aria-label="Match each item to its correct answer">
          <thead>
            <tr>
              <th scope="col" className="hotspot-col-description">Description / Task</th>
              <th scope="col" className="hotspot-col-answer">Answer</th>
              {result && <th scope="col" className="hotspot-col-status">Result</th>}
            </tr>
          </thead>
          <tbody>
            {slots.map((slot, idx) => {
              const slotResult = result?.slotResults?.find((r) => r.slot === slot);
              const rowClass = slotResult
                ? slotResult.isCorrect ? 'hotspot-row-correct' : 'hotspot-row-incorrect'
                : '';

              return (
                <tr key={idx} className={`hotspot-row ${rowClass}`}>
                  <td className="hotspot-cell-description">{slot}</td>
                  <td className="hotspot-cell-select">
                    <select
                      value={selections[slot]}
                      onChange={(e) => handleChange(slot, e.target.value)}
                      disabled={!!result || disabled}
                      aria-label={`Answer for: ${slot}`}
                      className={`hotspot-select ${
                        slotResult
                          ? slotResult.isCorrect ? 'select-correct' : 'select-incorrect'
                          : ''
                      }`}
                    >
                      <option value="">— Select —</option>
                      {options.map((opt, oi) => (
                        <option key={oi} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {slotResult && !slotResult.isCorrect && (
                      <div className="hotspot-correct-answer">
                        ✓ {slotResult.correct}
                      </div>
                    )}
                  </td>
                  {result && (
                    <td className="hotspot-cell-status" aria-label={slotResult?.isCorrect ? 'Correct' : 'Incorrect'}>
                      {slotResult?.isCorrect ? '✅' : '❌'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {result && (
        <div className={`hotspot-verdict ${result.isCorrect ? 'verdict-pass' : 'verdict-fail'}`}>
          {result.isCorrect
            ? '✅ All correct!'
            : `❌ ${result.slotResults.filter((r) => !r.isCorrect).length} of ${slots.length} incorrect`}
        </div>
      )}

      <div className="hotspot-actions">
        {!result && (
          <button
            className="hotspot-submit-btn"
            onClick={handleSubmit}
            disabled={!allSelected}
            aria-disabled={!allSelected}
          >
            Submit
          </button>
        )}
        {result && !disabled && (
          <button className="hotspot-reset-btn" onClick={handleReset}>
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}

export default HotspotQuestion;
